import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { Icon, materialWarningFill } from '@gs-ux-uitoolkit-react/icon';

/**
 * Renders the list of active maintenance messages inside the sign-in box.
 *
 * Per Figma Frame 2049772152 (2026 redesign): each message is a flat card
 * with [warning icon] header on one row and the body always visible below,
 * wrapped in the surface/functional/warning/bold border.
 */
export function MaintenanceMessage() {
    const [messages, setMessages] = useState<any[]>();

    useEffect(() => {
        const isAdvisorSite = window.location.hostname.includes('advisorsolutions');

        fetch('/api/v2/maintenance-messages')
            .then(response => response.json())
            .then(data => {
                // The API returns every message unfiltered; each site is responsible
                // for showing only the ones flagged for it.
                const items = Array.isArray(data) ? data : [];
                const filtered = items.filter(message =>
                    isAdvisorSite ? message.showOnAdvisorSite : message.showOnAdviseeSite
                );
                setMessages(filtered);
            })
            .catch(() => {
                setMessages([]);
            });
    }, []);

    if (!messages || messages.length === 0) {
        return null;
    }

    return (
        <div
            className="flex flex-col gap-3 w-full"
            data-qa-comp="maintenance-messages"
        >
            {messages.map((message: any, index: number) => (
                <FlatMessageCard message={message} messageIndex={index} key={index} />
            ))}
        </div>
    );
}

type Props = {
    message: any;
    messageIndex: number;
};

/**
 * 2026 redesign card — header + body, both always visible.
 */
function FlatMessageCard({ message, messageIndex }: Props) {
    const bodyRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const target = bodyRef.current;
        if (!target) {
            return;
        }
        target.innerHTML = message.messageBody
            ? DOMPurify.sanitize(message.messageBody)
            : '';
    }, [message.messageBody]);

    return (
        <div
            className="flex flex-col gap-1 p-3 rounded border border-yellow-30"
            data-qa-comp={`maintenance-message-${messageIndex}`}
        >
            <div className="flex flex-row gap-2 items-center">
                <Icon svg={materialWarningFill} className="text-yellow-50" aria-hidden />
                <span className="text-black text-sm font-normal leading-5">
                    {message.messageHeader}
                </span>
            </div>
            <div
                ref={bodyRef}
                className="text-black text-sm font-normal leading-5"
                data-qa-comp={`maintenance-message-body-${messageIndex}`}
            />
        </div>
    );
}
